import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, getEventMessages, sendEventMessage, deleteEventMessage } from '../lib/supabase';
import { firstName } from '../helpers';

const WA_SVG = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

function PostCard({ msg, canManage, onDelete }) {
  const time = new Date(msg.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const day  = (() => {
    const d = new Date(msg.created_at);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return null;
    if (d.toDateString() === yesterday.toDateString()) return 'Ieri';
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  })();

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ fontSize: 17, lineHeight: 1.6, color: 'var(--text)', wordBreak: 'break-word', marginBottom: 5 }}>
        {msg.message}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 16, color: 'var(--muted)' }}>
          {day ? `${day} · ${time}` : time}
        </div>
        {canManage && (
          <button onClick={() => onDelete(msg.id)}
            style={{ background: 'none', border: 'none', color: 'rgba(255,82,82,0.5)', fontSize: 16, cursor: 'pointer', padding: '2px 0 2px 12px', lineHeight: 1 }}>
            Elimina
          </button>
        )}
      </div>
    </div>
  );
}

function ChatSection({ eventId, currentUser, musicians, onShowMusician, onNewMessage, whatsappLink, canManage, onSaveWhatsappLink, manager }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [editingLink, setEditingLink] = useState(false);
  const [linkDraft, setLinkDraft] = useState(whatsappLink || '');
  const bottomRef = useRef();
  const lastCountRef = useRef(0);

  const load = useCallback(async () => {
    const msgs = await getEventMessages(eventId);
    if (onNewMessage && msgs.length > lastCountRef.current && lastCountRef.current > 0) onNewMessage();
    setMessages(msgs);
    lastCountRef.current = msgs.length;
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ch = supabase.channel(`chat-${eventId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'event_messages', filter: `event_id=eq.${eventId}` }, (payload) => {
        if (payload.new.post_type && payload.new.post_type !== 'avviso') return;
        setMessages(prev => prev.find(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'event_messages' }, (payload) => {
        setMessages(prev => prev.filter(m => m.id !== payload.old.id));
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [eventId]);

  // Rete di sicurezza nel caso il tempo reale non sia attivo su event_messages.
  // Era ogni 4 secondi: con dieci musicisti collegati facevano 150 interrogazioni
  // al minuto in piu' del necessario. Una volta verificato che il tempo reale
  // funziona (vedi supabase/functions.sql) questo blocco si puo' togliere.
  useEffect(() => {
    const iv = setInterval(async () => {
      const msgs = await getEventMessages(eventId);
      if (msgs.length !== lastCountRef.current) { setMessages(msgs); lastCountRef.current = msgs.length; }
    }, 20000);
    return () => clearInterval(iv);
  }, [eventId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleDelete = async (id) => {
    setMessages(prev => prev.filter(m => m.id !== id));
    await deleteEventMessage(id);
  };

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    const msg = text.trim();
    setText('');
    setSending(true);
    try {
      const sent = await sendEventMessage(eventId, currentUser.id, msg, 'avviso');
      if (sent) setMessages(prev => prev.find(m => m.id === sent.id) ? prev : [...prev, sent]);
    } finally { setSending(false); }
  };

  const handleSaveLink = async () => {
    await onSaveWhatsappLink(linkDraft.trim());
    setEditingLink(false);
  };

  const managerWA = manager?.whatsapp_link ||
    (manager?.phone ? `https://wa.me/${manager.phone.replace(/\D/g, '')}` : null);
  const showWASection = managerWA || whatsappLink || canManage;

  const SectionTitle = ({ children }) => (
    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
      {children}
    </div>
  );

  const WARow = ({ label, href, onModify }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
      <span style={{ fontSize: 18, fontWeight: 500, color: 'var(--text)' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <a href={href} target="_blank" rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
            background: '#25D366', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 16 }}>
          {WA_SVG} Apri
        </a>
        {onModify && (
          <button onClick={onModify}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 16, cursor: 'pointer', padding: '6px 4px' }}>
            Modifica
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Box Chat WhatsApp */}
      {showWASection && (
        <div style={{ borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', background: 'var(--surface)', padding: '14px 16px' }}>
          <SectionTitle>Chat</SectionTitle>

          {whatsappLink && !editingLink && (
            <WARow label="Chat evento" href={whatsappLink}
              onModify={canManage ? () => { setLinkDraft(whatsappLink); setEditingLink(true); } : null}
            />
          )}
          {whatsappLink && managerWA && (
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '2px 0' }}/>
          )}

          {managerWA && (
            <WARow
              label={manager?.name ? `Scrivi a ${firstName(manager.name)}` : 'Scrivi all\'organizzatore'}
              href={managerWA}
            />
          )}
          {!whatsappLink && !editingLink && canManage && (
            <button onClick={() => setEditingLink(true)}
              style={{ width: '100%', background: 'none', border: '1.5px dashed rgba(37,211,102,0.3)', color: '#25D366',
                fontSize: 16, cursor: 'pointer', padding: '10px 14px', borderRadius: 10, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, minHeight: 44 }}>
              {WA_SVG} Aggiungi link gruppo WhatsApp
            </button>
          )}
          {editingLink && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={linkDraft} onChange={e => setLinkDraft(e.target.value)}
                placeholder="https://chat.whatsapp.com/..."
                autoFocus
                style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '9px 12px', color: 'var(--text)', fontSize: 16, outline: 'none', minWidth: 0, fontFamily: 'inherit' }}
              />
              <button onClick={handleSaveLink}
                style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontWeight: 700, fontSize: 16, cursor: 'pointer', flexShrink: 0 }}>
                Salva
              </button>
              <button onClick={() => setEditingLink(false)}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 16, cursor: 'pointer', padding: '9px 4px', flexShrink: 0 }}>
                Annulla
              </button>
            </div>
          )}
        </div>
      )}

      {/* Box Avvisi */}
      <div style={{ borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', background: 'var(--surface)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 0' }}>
          <SectionTitle>Avvisi</SectionTitle>
        </div>

        <div style={{ maxHeight: 360, overflowY: 'auto', padding: '0 16px' }}>
          {messages.length === 0 && (
            <div style={{ padding: '20px 0 24px', color: 'var(--muted)', fontSize: 17 }}>
              {canManage ? 'Nessun avviso ancora. Pubblica il primo.' : 'Nessun avviso ancora.'}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id} style={{ borderBottom: i < messages.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
              <PostCard msg={m} canManage={canManage} onDelete={handleDelete} />
            </div>
          ))}
          <div ref={bottomRef}/>
        </div>

        {canManage && (
          <div style={{ padding: '12px 16px 14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <textarea
              placeholder="Scrivi un avviso per i musicisti..."
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              rows={2}
              style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '9px 13px', color: 'var(--text)', fontSize: 17, outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box', marginBottom: 8 }}
            />
            <button onClick={handleSend} disabled={!text.trim() || sending}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 17, transition: 'all .15s',
                cursor: text.trim() && !sending ? 'pointer' : 'default',
                background: text.trim() && !sending ? '#ED4D1D' : 'rgba(237,77,29,0.12)',
                color: text.trim() && !sending ? '#fff' : 'rgba(237,77,29,0.35)' }}>
              {sending ? <span className="spinner"/> : 'Pubblica avviso'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatSection;
